/**
 * Small presentational primitives shared by the workbench pages.
 *
 * These exist to keep dense tables and panels visually consistent, not to form a
 * general-purpose design system. Anything used by a single page lives with that page.
 */

import type { ReactNode } from 'react';

export type BadgeTone = 'ok' | 'warn' | 'danger' | 'info' | 'neutral';

export interface BadgeProps {
  readonly tone: BadgeTone;
  readonly children: ReactNode;
}

export function Badge({ tone, children }: BadgeProps): JSX.Element {
  return <span className={`badge badge--${tone}`}>{children}</span>;
}

export interface PanelProps {
  readonly title?: ReactNode;
  readonly subtitle?: ReactNode;
  readonly actions?: ReactNode;
  /** Removes inner padding for tables that manage their own cell spacing. */
  readonly flush?: boolean;
  readonly children: ReactNode;
}

export function Panel({ title, subtitle, actions, flush, children }: PanelProps): JSX.Element {
  const hasHead = title !== undefined || actions !== undefined;
  return (
    <section className="panel">
      {hasHead ? (
        <header className="panel__head">
          {title !== undefined ? <h2 className="panel__title">{title}</h2> : null}
          {subtitle !== undefined ? <span className="panel__sub">{subtitle}</span> : null}
          {actions !== undefined ? <div className="panel__actions">{actions}</div> : null}
        </header>
      ) : null}
      <div className={flush === true ? 'panel__body panel__body--flush' : 'panel__body'}>
        {children}
      </div>
    </section>
  );
}

export interface StatCell {
  readonly label: string;
  readonly value: ReactNode;
  readonly hint?: ReactNode;
}

export interface StatBarProps {
  readonly cells: readonly StatCell[];
}

/**
 * A strip of labelled figures separated by hairlines. Chosen over individual
 * bordered cards so the first screen stays dense and scannable.
 */
export function StatBar({ cells }: StatBarProps): JSX.Element {
  return (
    <div className="statbar">
      {cells.map((cell, index) => (
        <div
          className="statbar__cell"
          key={cell.label}
          style={{ animationDelay: `${String(index * 60)}ms` }}
        >
          <div className="statbar__label">{cell.label}</div>
          <div className="statbar__value">
            {typeof cell.value === 'string' || typeof cell.value === 'number' ? (
              // Keyed by value so a changed figure replays the entry animation.
              <span className="statbar__figure" key={String(cell.value)}>
                {cell.value}
              </span>
            ) : (
              cell.value
            )}
          </div>
          {cell.hint !== undefined ? <div className="statbar__hint">{cell.hint}</div> : null}
        </div>
      ))}
    </div>
  );
}

export type NoticeTone = 'error' | 'warn' | 'info' | 'neutral';

export interface NoticeProps {
  readonly tone: NoticeTone;
  readonly title: ReactNode;
  readonly detail?: ReactNode;
  readonly actions?: ReactNode;
}

const NOTICE_ICONS: Readonly<Record<NoticeTone, string>> = {
  error: '!',
  warn: '!',
  info: 'i',
  neutral: 'i',
};

export function Notice({ tone, title, detail, actions }: NoticeProps): JSX.Element {
  const className = tone === 'neutral' ? 'notice' : `notice notice--${tone}`;
  return (
    <div className={className} role={tone === 'error' ? 'alert' : undefined}>
      <span className="notice__icon" aria-hidden="true">
        {NOTICE_ICONS[tone]}
      </span>
      <div className="notice__body">
        <div className="notice__title">{title}</div>
        {detail !== undefined ? <div className="notice__detail">{detail}</div> : null}
        {actions !== undefined ? <div className="state__actions">{actions}</div> : null}
      </div>
    </div>
  );
}

export interface EmptyStateProps {
  readonly title: string;
  readonly detail?: ReactNode;
  readonly actions?: ReactNode;
}

export function EmptyState({ title, detail, actions }: EmptyStateProps): JSX.Element {
  return (
    <div className="state">
      <div className="state__title">{title}</div>
      {detail !== undefined ? <div>{detail}</div> : null}
      {actions !== undefined ? <div className="state__actions">{actions}</div> : null}
    </div>
  );
}

export interface TableSkeletonProps {
  readonly rows?: number;
}

export function TableSkeleton({ rows = 5 }: TableSkeletonProps): JSX.Element {
  return (
    <div className="skeleton-row" aria-hidden="true">
      {Array.from({ length: rows }, (_, index) => (
        <div
          className="skeleton-bar"
          key={index}
          style={{ width: `${String(58 + ((index * 13) % 38))}%` }}
        />
      ))}
    </div>
  );
}

export {
  ConfirmDialog,
  Skeleton,
  SegmentedControl,
  RefreshIndicator,
  ToastViewport,
} from './feedback';
export type { SkeletonKind } from './feedback';

// Note: useConfirm, toast, useToasts, useNewIds are exported from their respective modules
// to satisfy Fast Refresh constraints. Import directly from:
// - './useConfirm' for useConfirm
// - './toastStore' for toast, useToasts, useNewIds

/** Accessible loading indicator with a status role for screen readers. */
export function LoadingBlock({ label }: { readonly label: string }): JSX.Element {
  return (
    <div aria-busy="true" aria-live="polite">
      <span className="state faint" style={{ padding: 0 }}>
        {label}
      </span>
      <TableSkeleton />
    </div>
  );
}
